import { CalendarMonthYearNav } from "@ui/components/CalendarNav";
import { calendar, dayChip } from "@ui/components/StandardCalendar/calendar.styles";
import {
  RangeCalendar,
  RangeCalendarCell,
  RangeCalendarGrid,
  RangeCalendarGridBody,
  RangeCalendarGridHeader,
  RangeCalendarHeaderCell,
  type RangeCalendarProps,
} from "@ui/primitives/RangeCalendar";
import { composeRenderProps, type DateValue } from "@ui/primitives/shared";
import { cx } from "@ui/utils/cx";

export interface StandardRangeCalendarProps<T extends DateValue> extends RangeCalendarProps<T> {}

export function StandardRangeCalendar<T extends DateValue>(props: StandardRangeCalendarProps<T>) {
  const styles = calendar();

  return (
    <RangeCalendar
      {...props}
      className={composeRenderProps(props.className, (className) => styles.root({ className }))}
    >
      <CalendarMonthYearNav visibleYears={40} />
      <RangeCalendarGrid weekdayStyle="short" className={styles.grid()}>
        <RangeCalendarGridHeader>
          {(day) => <RangeCalendarHeaderCell className={styles.headerCell()}>{day}</RangeCalendarHeaderCell>}
        </RangeCalendarGridHeader>
        <RangeCalendarGridBody>
          {(date) => (
            <RangeCalendarCell date={date} className={styles.cell()}>
              {({
                formattedDate,
                isSelected,
                isSelectionStart,
                isSelectionEnd,
                isDisabled,
                isUnavailable,
                isOutsideMonth,
              }) => {
                const isPill = isSelectionStart || isSelectionEnd;
                const showRange = isSelected && !(isSelectionStart && isSelectionEnd);
                const isStartOnly = isSelectionStart && !isSelectionEnd;
                const isEndOnly = isSelectionEnd && !isSelectionStart;

                return (
                  <>
                    {showRange && (
                      <div
                        className={cx(
                          "absolute top-1/2 h-8 -translate-y-1/2 bg-green-100",
                          isStartOnly && "right-0 left-1 rounded-l-lg",
                          isEndOnly && "right-1 left-0 rounded-r-lg",
                          !isStartOnly && !isEndOnly && "right-0 left-0",
                        )}
                      />
                    )}
                    <div
                      className={dayChip({
                        isPill,
                        isInRange: isSelected,
                        isDisabled,
                        isUnavailable,
                        isOutsideMonth,
                      })}
                    >
                      {formattedDate}
                    </div>
                  </>
                );
              }}
            </RangeCalendarCell>
          )}
        </RangeCalendarGridBody>
      </RangeCalendarGrid>
    </RangeCalendar>
  );
}
